// ===========================================
// BUSTED! BLACKJACK - CLIENT TABLE MASTER
// ===========================================
// Client-side EVENT ORCHESTRATOR - the "welcome committee" for game events.
//
// ARCHITECTURE:
// - Socket delivers batches to ClientTableMaster FIRST
// - ClientTableMaster unpacks batches into individual events
// - Events are queued and sorted by ROUND/PHASE/SEQUENCE
// - Events are released one-by-one to useGameEvents via callback
// - Stale events (seq <= lastProcessed) are discarded with diagnostics
//
// useGameEvents becomes a DUMB EXECUTOR - no batch handling, no guards.
// ClientTableMaster ENFORCES the expected order of operations.
//
// See: docs/TABLEMASTER_DESIGN.md
// ===========================================

import {
  GamePhase,
  BlackjackEvent,
  EventKey,
  BatchKey,
  KeyedEvent,
  KeyedBatch,
  TableMasterState,
  ValidationResult,
  RejectedRecord,
  validateBatchKey,
  validateEventKey,
  validateBatchConsistency,
  formatBatchKey,
  formatEventKey,
  createInitialTableMasterState,
  updateStateAfterBatch,
  resetStateForNewRound,
} from '@busted-blackjack/shared';
import { diagLog } from './diagnosticLogger';
import { useAuthStore } from '../stores/authStore';
import { recordSkippedEvent } from '../stores/adminLogStore';

// ===========================================
// TYPES
// ===========================================

export interface ClientTableMasterOptions {
  /** Called when a sorted batch of events is ready for processing */
  onBatchReady?: (events: BlackjackEvent[], batchKey: string) => void;
  /** Called when a round ACK should be sent to the server */
  onRoundAck?: (round: number, batchSequence: number) => void;
  /** Enable verbose logging */
  debug?: boolean;
}

/**
 * Pending round ACK - to be sent after ROUND_COMPLETED is processed.
 */
interface PendingRoundAck {
  round: number;
  batchSequence: number;
}

/**
 * Get numeric order for a phase (for sorting).
 */
function getPhaseOrder(phase: GamePhase): number {
  switch (phase) {
    case GamePhase.WAITING: return 0;
    case GamePhase.BETTING: return 1;
    case GamePhase.DEALING: return 2;
    case GamePhase.PLAYING: return 3;
    case GamePhase.DEALER_TURN: return 4;
    case GamePhase.RESOLVING: return 5;
    default: return 9;
  }
}

/**
 * Compute sort order for an event.
 * Format: round * 1_000_000 + phaseOrder * 10_000 + sequence
 */
function computeSortOrder(round: number, phase: GamePhase, sequence: number): number {
  const phaseOrder = getPhaseOrder(phase);
  return round * 1_000_000 + phaseOrder * 10_000 + sequence;
}

// ===========================================
// BALANCE TRACKER
// ===========================================
// Delays balance display updates until chip animations complete.
// Server's balance_update provides authoritative values.
//
// Flow:
// 1. Chip animation starts → registerAnimation()
// 2. Server sends balance_update → queueServerBalance() (value is held)
// 3. Chip animation ends → animationComplete() (apply held balance)
// 4. If no animations pending, balance updates immediately
// ===========================================

class BalanceTracker {
  /** Number of chip animations currently in flight */
  private animationsInFlight: number = 0;
  /** Last known server-authoritative balance (may be held) */
  private serverBalance: number | null = null;
  /** Whether we have a pending balance to apply */
  private hasPendingBalance: boolean = false;
  /** Debug mode */
  private debug: boolean;

  constructor(debug = false) {
    this.debug = debug;
  }

  /**
   * Register that a chip animation is starting.
   * Balance updates will be held until all animations complete.
   */
  registerAnimation(eventType: string, amount: number, seatPosition?: number): void {
    this.animationsInFlight++;
    this.log(`Animation started: ${eventType} (amount: ${amount}, seat: ${seatPosition}) - ${this.animationsInFlight} in flight`);
  }

  /**
   * Mark a chip animation as complete.
   * If this was the last animation, apply any pending balance update.
   */
  animationComplete(): void {
    this.animationsInFlight = Math.max(0, this.animationsInFlight - 1);
    this.log(`Animation complete - ${this.animationsInFlight} remaining`);

    if (this.animationsInFlight === 0 && this.hasPendingBalance && this.serverBalance !== null) {
      this.log(`All animations complete, applying pending balance: ${this.serverBalance}`);
      useAuthStore.getState().updateBalance(this.serverBalance);
      this.hasPendingBalance = false;
    }
  }

  /**
   * Queue a server balance update.
   * If animations are in flight, the update is held until they complete.
   * If no animations, the update is applied immediately.
   */
  queueServerBalance(serverBalance: number): void {
    this.serverBalance = serverBalance;

    if (this.animationsInFlight === 0) {
      // No animations in flight, apply immediately
      this.log(`No animations, applying balance immediately: ${serverBalance}`);
      useAuthStore.getState().updateBalance(serverBalance);
      this.hasPendingBalance = false;
    } else {
      // Hold the balance until animations complete
      this.log(`Holding balance update (${this.animationsInFlight} animations in flight): ${serverBalance}`);
      this.hasPendingBalance = true;
    }
  }

  /**
   * Force apply any pending balance (e.g., on round end, table change).
   * Clears all state.
   */
  flush(): void {
    if (this.hasPendingBalance && this.serverBalance !== null) {
      this.log(`Flushing pending balance: ${this.serverBalance}`);
      useAuthStore.getState().updateBalance(this.serverBalance);
    }
    this.animationsInFlight = 0;
    this.hasPendingBalance = false;
    // Keep serverBalance for debugging
  }

  /**
   * Clear pending animations (e.g., on table change).
   */
  clearPending(): void {
    this.animationsInFlight = 0;
    this.hasPendingBalance = false;
  }

  /**
   * Get summary for debugging.
   */
  getSummary(): {
    animationsInFlight: number;
    hasPendingBalance: boolean;
    serverBalance: number | null;
  } {
    return {
      animationsInFlight: this.animationsInFlight,
      hasPendingBalance: this.hasPendingBalance,
      serverBalance: this.serverBalance,
    };
  }

  private log(...args: unknown[]): void {
    if (this.debug) {
      console.log('[BalanceTracker]', ...args);
    }
  }
}

// ===========================================
// CLIENT TABLE MASTER CLASS
// ===========================================

export class ClientTableMaster {
  // State tracking
  private state: TableMasterState;
  private isEnabled: boolean = true;
  private debug: boolean;

  // Sequence tracking per round (for stale event detection)
  private lastProcessedRound: number = 0;
  private lastProcessedSequence: number = 0;

  // ===========================================
  // ROUND QUEUING - Hold future-round events until current round completes
  // ===========================================
  // The active round is the one currently being processed by useGameEvents.
  // Events for round N+1 are queued until round N's ROUND_COMPLETED is processed.
  // This eliminates race conditions between rounds.
  private activeProcessingRound: number = 0;
  private futureRoundQueue: Map<number, KeyedBatch[]> = new Map();

  // ===========================================
  // AWAY MODE - Pause processing for resync
  // ===========================================
  // When in away mode (tab hidden, reconnecting, etc.), all batches are queued
  // and not processed. On exit, batches are discarded since resync provides
  // fresh state from the server.
  private awayMode: boolean = false;
  private awayModeQueue: KeyedBatch[] = [];

  // Diagnostics
  private discardedEvents: Array<{
    event: BlackjackEvent;
    reason: string;
    timestamp: number;
  }> = [];

  // ===========================================
  // SERVER SKIP LOGGING - Track skipped events for analytics
  // ===========================================
  // These records are batched and sent to the server periodically.
  // Server aggregates them to identify patterns (reconnection issues,
  // server ordering bugs, etc.)
  private skippedEventsForServer: Array<{
    type: string;
    reason: string;
    eventSeq: number;
    lastProcessedSeq: number;
    eventRound: number;
    activeRound: number;
    timestamp: number;
    tableId?: string;
  }> = [];
  private skipLogFlushTimer: ReturnType<typeof setTimeout> | null = null;
  private onSkipLogFlush?: (records: typeof this.skippedEventsForServer) => void;

  // Balance tracking
  private balanceTracker: BalanceTracker;

  // Callbacks
  private onBatchReady?: (events: BlackjackEvent[], batchKey: string) => void;
  private onRoundAck?: (round: number, batchSequence: number) => void;

  // Round ACK tracking (backpressure control)
  private lastSentRoundAck: number = 0;
  private pendingRoundAck: PendingRoundAck | null = null;

  constructor(options?: ClientTableMasterOptions) {
    this.state = createInitialTableMasterState();
    this.onBatchReady = options?.onBatchReady;
    this.onRoundAck = options?.onRoundAck;
    this.debug = options?.debug ?? false;
    this.balanceTracker = new BalanceTracker(this.debug);

    this.log('ClientTableMaster initialized - BATCH ORCHESTRATOR MODE');
  }

  // ===========================================
  // PUBLIC API - BATCH RECEPTION
  // ===========================================

  /**
   * Receive a keyed batch from the server.
   * Sorts events by ROUND/PHASE/SEQUENCE and releases as a sorted batch.
   *
   * THIS IS THE ENTRY POINT - socket delivers batches here FIRST.
   */
  receiveBatch(batch: KeyedBatch): void {
    // DIAGNOSTIC: Log exact receive timestamp and sequence info
    const recvTimestamp = Date.now();
    const eventSeqs = batch.events.map(e => e.key.sequence).join(',');
    const eventTypes = batch.events.map(e => e.type).join(',');
    console.log(`[BATCH_RECV] ts=${recvTimestamp} round=${batch.key.round} batchSeq=${batch.key.batchSequence} eventSeqs=[${eventSeqs}] types=[${eventTypes}] activeRound=${this.activeProcessingRound}`);

    this.log('Received batch:', formatBatchKey(batch.key), 'with', batch.events.length, 'events');

    // Log to diagnostic system
    diagLog.recv({
      seq: batch.key.batchSequence,
      type: 'KEYED_BATCH',
      details: {
        round: batch.key.round,
        phase: batch.key.phase,
        eventCount: batch.events.length,
        eventTypes: batch.events.map(e => e.type).join(','),
        activeProcessingRound: this.activeProcessingRound,
      },
    });

    const batchRound = batch.key.round;

    // ===========================================
    // AWAY MODE - Queue all batches during resync
    // ===========================================
    // When in away mode, queue batches without processing. These will be
    // discarded when resync completes with fresh state from the server.
    if (this.awayMode) {
      this.log(`AWAY MODE: Queuing batch for round ${batchRound}`);
      console.log(`[ClientTableMaster] AWAY MODE: Queuing batch R${batchRound} (will be discarded on resync)`);
      this.awayModeQueue.push(batch);
      return; // Don't process while in away mode
    }

    // ===========================================
    // ROUND QUEUING LOGIC
    // ===========================================
    // If this batch is for a FUTURE round, queue it instead of releasing.
    // Events will be released when the current round completes.
    if (batchRound > this.activeProcessingRound && this.activeProcessingRound > 0) {
      this.log(`QUEUING batch for future round ${batchRound} (active round is ${this.activeProcessingRound})`);
      console.log(`[ClientTableMaster] QUEUING R${batchRound} batch - active round is R${this.activeProcessingRound}`);

      const queuedBatches = this.futureRoundQueue.get(batchRound) || [];
      queuedBatches.push(batch);
      this.futureRoundQueue.set(batchRound, queuedBatches);

      diagLog.recv({
        seq: batch.key.batchSequence,
        type: 'BATCH_QUEUED',
        details: {
          round: batchRound,
          queuedBatchCount: queuedBatches.length,
          activeProcessingRound: this.activeProcessingRound,
        },
      });
      return; // Don't release yet
    }

    // If this is the first batch (activeProcessingRound is 0), set the active round
    if (this.activeProcessingRound === 0) {
      this.activeProcessingRound = batchRound;
      this.log(`Setting initial activeProcessingRound to ${batchRound}`);
    }

    // Track if this batch contains ROUND_COMPLETED (for backpressure ACK)
    const hasRoundCompleted = batch.events.some(e => e.type === 'ROUND_COMPLETED');
    if (hasRoundCompleted) {
      this.pendingRoundAck = {
        round: batch.key.round,
        batchSequence: batch.key.batchSequence,
      };
      this.log(`Queued round ACK for round ${batch.key.round}, batch ${batch.key.batchSequence}`);
    }

    // Update state
    const lastEvent = batch.events[batch.events.length - 1];
    this.state = updateStateAfterBatch(this.state, batch.key, lastEvent?.key);

    // ===========================================
    // SORT EVENTS AND RELEASE AS BATCH
    // ===========================================
    this.releaseBatch(batch);
  }

  /**
   * Internal: Release a batch to the handler.
   * Extracts events, sorts them, filters stale, and calls onBatchReady.
   */
  private releaseBatch(batch: KeyedBatch): void {
    // Extract raw events and sort by ROUND/PHASE/SEQUENCE
    const eventsWithKeys = batch.events.map(keyedEvent => ({
      event: keyedEvent.payload as BlackjackEvent,
      key: keyedEvent.key,
      sortOrder: computeSortOrder(keyedEvent.key.round, keyedEvent.key.phase, keyedEvent.key.sequence),
    }));

    // Sort by sortOrder (ROUND/PHASE/SEQUENCE)
    eventsWithKeys.sort((a, b) => a.sortOrder - b.sortOrder);

    // Filter out stale events and track highest sequence for deduplication
    const validEvents: BlackjackEvent[] = [];
    let highestSequenceInBatch = this.lastProcessedSequence;
    let highestRoundInBatch = this.lastProcessedRound;

    for (const { event, key } of eventsWithKeys) {
      if (this.isStaleEvent(key.round, key.sequence)) {
        this.recordDiscard(event, `STALE: round=${key.round} seq=${key.sequence} <= lastProcessed R${this.lastProcessedRound} seq=${this.lastProcessedSequence}`);
      } else {
        validEvents.push(event);
        // Track highest sequence we're about to process
        if (key.round > highestRoundInBatch ||
            (key.round === highestRoundInBatch && key.sequence > highestSequenceInBatch)) {
          highestRoundInBatch = key.round;
          highestSequenceInBatch = key.sequence;
        }
      }
    }

    // Release sorted batch to handler
    if (validEvents.length > 0 && this.onBatchReady) {
      const batchKey = `${batch.key.round}-${batch.key.batchSequence}`;
      this.log(`Releasing ${validEvents.length} sorted events (filtered ${eventsWithKeys.length - validEvents.length} stale)`);
      this.onBatchReady(validEvents, batchKey);

      // Update lastProcessed AFTER releasing to handler
      // This prevents duplicate processing if the same batch arrives again
      this.lastProcessedRound = highestRoundInBatch;
      this.lastProcessedSequence = highestSequenceInBatch;
      this.log(`Updated lastProcessed to R${this.lastProcessedRound} seq=${this.lastProcessedSequence}`);
    }
  }

  /**
   * Signal that a round has completed processing.
   * This releases any queued batches for the next round.
   * Call this from useGameEvents after ROUND_COMPLETED is fully processed.
   */
  roundCompleted(completedRound: number): void {
    this.log(`Round ${completedRound} completed, checking for queued batches`);
    console.log(`[ClientTableMaster] Round ${completedRound} completed. Active was R${this.activeProcessingRound}. Queue has rounds: [${Array.from(this.futureRoundQueue.keys()).join(', ')}]`);

    // Move to next round
    const nextRound = completedRound + 1;
    this.activeProcessingRound = nextRound;
    this.lastProcessedSequence = 0; // Reset sequence for new round

    // Check if we have queued batches for the next round
    const queuedBatches = this.futureRoundQueue.get(nextRound);
    if (queuedBatches && queuedBatches.length > 0) {
      this.log(`Releasing ${queuedBatches.length} queued batches for round ${nextRound}`);
      console.log(`[ClientTableMaster] RELEASING ${queuedBatches.length} queued batches for R${nextRound}`);

      // Sort batches by batchSequence before releasing
      queuedBatches.sort((a, b) => a.key.batchSequence - b.key.batchSequence);

      // Release each batch
      for (const batch of queuedBatches) {
        // Track if this batch contains ROUND_COMPLETED
        const hasRoundCompleted = batch.events.some(e => e.type === 'ROUND_COMPLETED');
        if (hasRoundCompleted) {
          this.pendingRoundAck = {
            round: batch.key.round,
            batchSequence: batch.key.batchSequence,
          };
        }

        // Update state
        const lastEvent = batch.events[batch.events.length - 1];
        this.state = updateStateAfterBatch(this.state, batch.key, lastEvent?.key);

        this.releaseBatch(batch);
      }

      // Clear the queue for this round
      this.futureRoundQueue.delete(nextRound);
    } else {
      this.log(`No queued batches for round ${nextRound}`);
    }
  }

  /**
   * Get the number of queued future-round batches (for diagnostics).
   */
  getQueuedBatchCount(): number {
    let count = 0;
    for (const batches of this.futureRoundQueue.values()) {
      count += batches.length;
    }
    return count;
  }

  /**
   * Set the batch handler callback.
   * Call this before receiving any batches.
   */
  setBatchHandler(handler: (events: BlackjackEvent[], batchKey: string) => void): void {
    this.onBatchReady = handler;
    this.log('Batch handler registered');
  }

  /**
   * Set the round ACK callback.
   */
  setRoundAckHandler(handler: (round: number, batchSequence: number) => void): void {
    this.onRoundAck = handler;
    this.log('Round ACK handler registered');
  }

  // ===========================================
  // PUBLIC API - STATE & DIAGNOSTICS
  // ===========================================

  /**
   * Sync state with game state store (e.g., on reconnection or joining new table).
   */
  syncState(round: number, phase: GamePhase, lastEventSequence?: number): void {
    const oldRound = this.state.currentRound;

    if (round !== oldRound) {
      this.log('State sync:', `R${oldRound}`, '->', `R${round}`);

      // Reset state for ANY round change (new round forward OR joining new table with lower round)
      this.state = resetStateForNewRound(this.state, round);
      this.lastProcessedRound = round;
      this.lastProcessedSequence = lastEventSequence ?? 0;
      this.activeProcessingRound = round;

      // Clear any stale queued batches (for rounds <= the new round)
      for (const queuedRound of this.futureRoundQueue.keys()) {
        if (queuedRound <= round) {
          this.log(`Clearing stale queued batches for round ${queuedRound}`);
          this.futureRoundQueue.delete(queuedRound);
        }
      }

      this.log(`Reset lastProcessed to R${this.lastProcessedRound} seq=${this.lastProcessedSequence}, activeProcessingRound=${this.activeProcessingRound}`);
    }

    this.state.currentRound = round;
    this.state.currentPhase = phase;

    if (lastEventSequence !== undefined) {
      this.lastProcessedSequence = lastEventSequence;
      this.log('Synced lastProcessedSequence to', lastEventSequence);
    }
  }

  /**
   * Reset for joining a new table (clears stale event tracking).
   * Also exits away mode since fresh state is coming from server.
   */
  resetForNewTable(): void {
    this.log('Resetting for new table');
    this.lastProcessedRound = 0;
    this.lastProcessedSequence = 0;
    this.activeProcessingRound = 0;
    this.futureRoundQueue.clear();
    this.discardedEvents = [];
    this.balanceTracker.clearPending();
    this.state = createInitialTableMasterState();
    // Exit away mode if active (fresh state coming from table_joined)
    if (this.awayMode) {
      this.log('Exiting away mode as part of table reset');
      this.awayModeQueue = [];
      this.awayMode = false;
    }
  }

  /**
   * Get current state.
   */
  getState(): TableMasterState {
    return { ...this.state };
  }

  /**
   * Get queue size (deprecated - always returns 0 in batch mode).
   */
  getQueueSize(): number {
    return 0;
  }

  /**
   * Get discarded events (for diagnostics).
   */
  getDiscardedEvents(): Array<{ event: BlackjackEvent; reason: string; timestamp: number }> {
    return [...this.discardedEvents];
  }

  /**
   * Get summary for debugging.
   */
  getSummary(): {
    currentRound: number;
    currentPhase: GamePhase;
    lastProcessedRound: number;
    lastProcessedSequence: number;
    activeProcessingRound: number;
    queuedBatchCount: number;
    queuedRounds: number[];
    discardedCount: number;
    balanceTracker: ReturnType<BalanceTracker['getSummary']>;
    awayMode: boolean;
    awayModeQueueCount: number;
    role: string;
  } {
    return {
      currentRound: this.state.currentRound,
      currentPhase: this.state.currentPhase,
      lastProcessedRound: this.lastProcessedRound,
      lastProcessedSequence: this.lastProcessedSequence,
      activeProcessingRound: this.activeProcessingRound,
      queuedBatchCount: this.getQueuedBatchCount(),
      queuedRounds: Array.from(this.futureRoundQueue.keys()),
      discardedCount: this.discardedEvents.length,
      balanceTracker: this.balanceTracker.getSummary(),
      awayMode: this.awayMode,
      awayModeQueueCount: this.awayModeQueue.length,
      role: 'BATCH_ORCHESTRATOR',
    };
  }

  /**
   * Clear history/diagnostics.
   */
  clearHistory(): void {
    this.discardedEvents = [];
    this.skippedEventsForServer = [];
    this.balanceTracker.clearPending();
  }

  // ===========================================
  // PUBLIC API - SKIP LOG REPORTING
  // ===========================================

  /**
   * Set the callback for flushing skip logs to the server.
   * Called when enough skips accumulate or periodically.
   */
  setSkipLogFlushHandler(handler: (records: typeof this.skippedEventsForServer) => void): void {
    this.onSkipLogFlush = handler;
  }

  /**
   * Force flush any pending skip logs to the server.
   */
  flushSkipLog(): void {
    if (this.skippedEventsForServer.length === 0) return;

    if (this.onSkipLogFlush) {
      this.log(`Flushing ${this.skippedEventsForServer.length} skip records to server`);
      this.onSkipLogFlush([...this.skippedEventsForServer]);
    }

    this.skippedEventsForServer = [];

    // Clear the timer if it exists
    if (this.skipLogFlushTimer) {
      clearTimeout(this.skipLogFlushTimer);
      this.skipLogFlushTimer = null;
    }
  }

  /**
   * Get pending skip log count (for diagnostics).
   */
  getSkipLogCount(): number {
    return this.skippedEventsForServer.length;
  }

  /**
   * Enable/disable the orchestrator.
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    this.log('Enabled:', enabled);
  }

  /**
   * Check if enabled.
   */
  getEnabled(): boolean {
    return this.isEnabled;
  }

  // ===========================================
  // PUBLIC API - AWAY MODE (Resync Support)
  // ===========================================

  /**
   * Enter away mode - pause event processing for resync.
   * All incoming batches will be queued and discarded when resync completes.
   * Use this when:
   * - Tab becomes hidden (visibility change)
   * - Network disconnects
   * - Manual resync requested
   */
  enterAwayMode(): void {
    if (this.awayMode) {
      this.log('Already in away mode');
      return;
    }
    this.awayMode = true;
    this.awayModeQueue = [];
    this.log('ENTERED away mode - batches will be queued and discarded');
    console.log('[ClientTableMaster] ENTERED away mode - event processing paused');
  }

  /**
   * Exit away mode after resync completes.
   * Discards all queued batches (fresh state from server replaces them).
   * Optionally syncs state from the new table_state.
   *
   * @param newRound - The round number from table_state to sync to
   * @param newPhase - The phase from table_state to sync to
   */
  exitAwayMode(newRound?: number, newPhase?: GamePhase): void {
    if (!this.awayMode) {
      this.log('Not in away mode');
      return;
    }

    const discardedCount = this.awayModeQueue.length;
    this.awayModeQueue = [];
    this.awayMode = false;

    // Sync state if provided
    if (newRound !== undefined && newPhase !== undefined) {
      this.syncState(newRound, newPhase);
      this.log(`Synced to R${newRound} phase=${newPhase}`);
    }

    this.log(`EXITED away mode - discarded ${discardedCount} queued batches`);
    console.log(`[ClientTableMaster] EXITED away mode - discarded ${discardedCount} batches, resumed processing`);
  }

  /**
   * Check if in away mode.
   */
  isInAwayMode(): boolean {
    return this.awayMode;
  }

  /**
   * Get count of batches queued during away mode (for diagnostics).
   */
  getAwayModeQueueCount(): number {
    return this.awayModeQueue.length;
  }

  // ===========================================
  // PUBLIC API - BALANCE TRACKING
  // ===========================================

  /**
   * Register that a chip animation is starting.
   */
  registerBalanceAnimation(eventType: string, amount: number, seatPosition?: number): void {
    this.balanceTracker.registerAnimation(eventType, amount, seatPosition);
  }

  /**
   * Mark a chip animation as complete.
   */
  balanceAnimationComplete(): void {
    this.balanceTracker.animationComplete();
  }

  /**
   * Queue a server balance update.
   */
  queueServerBalance(balance: number): void {
    this.balanceTracker.queueServerBalance(balance);
  }

  /**
   * Flush any pending balance.
   */
  flushBalance(): void {
    this.balanceTracker.flush();
  }

  // ===========================================
  // PUBLIC API - ROUND ACK
  // ===========================================

  /**
   * Send any pending round ACK.
   * Call this after event processing completes (e.g., after ROUND_COMPLETED animation).
   */
  sendPendingRoundAck(): void {
    if (!this.pendingRoundAck) return;

    const { round, batchSequence } = this.pendingRoundAck;

    if (this.onRoundAck) {
      this.log(`Sending round ACK: round=${round}, batch=${batchSequence}`);
      this.onRoundAck(round, batchSequence);
      this.lastSentRoundAck = round;
    } else {
      this.log(`No ACK callback registered, skipping ACK for round ${round}`);
    }

    this.pendingRoundAck = null;
  }

  /**
   * Check if there's a pending round ACK.
   */
  hasPendingRoundAck(): boolean {
    return this.pendingRoundAck !== null;
  }

  /**
   * Get the last sent round ACK number.
   */
  getLastSentRoundAck(): number {
    return this.lastSentRoundAck;
  }

  // ===========================================
  // PRIVATE - STALE EVENT DETECTION
  // ===========================================

  /**
   * Check if an event is stale (already processed).
   */
  private isStaleEvent(round: number, sequence: number): boolean {
    // Event from a previous round
    if (round < this.lastProcessedRound) {
      return true;
    }

    // Event from same round but already processed sequence
    if (round === this.lastProcessedRound && sequence <= this.lastProcessedSequence) {
      return true;
    }

    return false;
  }

  /**
   * Record a discarded event for diagnostics.
   */
  private recordDiscard(event: BlackjackEvent, reason: string): void {
    this.log(`DISCARDED: ${event.type} - ${reason}`);

    diagLog.recv({
      seq: event.sequenceNumber,
      type: event.type,
      details: { DISCARDED: true, reason },
    });

    this.discardedEvents.push({
      event,
      reason,
      timestamp: Date.now(),
    });

    // Keep only last 100
    if (this.discardedEvents.length > 100) {
      this.discardedEvents.shift();
    }

    // ===========================================
    // SERVER SKIP LOGGING
    // ===========================================
    // Record for server-side analytics
    this.recordSkipForServer(event, reason);
  }

  /**
   * Record a skipped event for server-side analytics.
   * Batches records and flushes periodically or when threshold is reached.
   */
  private recordSkipForServer(event: BlackjackEvent, reason: string): void {
    // Extract round number from roundId if available
    const roundMatch = event.roundId?.match(/-(\d+)-\d+$/);
    const eventRound = roundMatch ? parseInt(roundMatch[1], 10) : 0;

    // Extract tableId from roundId (format: "tableId-roundNumber-timestamp")
    const tableIdMatch = event.roundId?.match(/^(.+)-\d+-\d+$/);
    const tableId = tableIdMatch ? tableIdMatch[1] : undefined;

    // Determine skip reason type for admin panel
    const skipReason: 'stale' | 'duplicate' | 'future_round' =
      reason.includes('STALE') ? 'stale' :
      reason.includes('DUPLICATE') ? 'duplicate' : 'future_round';

    // Log to admin panel for immediate visibility
    recordSkippedEvent({
      reason: skipReason,
      eventType: event.type,
      eventSeq: event.sequenceNumber,
      lastProcessedSeq: this.lastProcessedSequence,
      eventRound,
      activeRound: this.activeProcessingRound,
    });

    // Batch for server-side analytics
    this.skippedEventsForServer.push({
      type: event.type,
      reason,
      eventSeq: event.sequenceNumber,
      lastProcessedSeq: this.lastProcessedSequence,
      eventRound,
      activeRound: this.activeProcessingRound,
      timestamp: Date.now(),
      tableId,
    });

    // Flush when we hit 10 records or schedule a delayed flush
    if (this.skippedEventsForServer.length >= 10) {
      this.flushSkipLog();
    } else if (!this.skipLogFlushTimer) {
      // Schedule flush after 30 seconds if we have pending records
      this.skipLogFlushTimer = setTimeout(() => {
        this.skipLogFlushTimer = null;
        this.flushSkipLog();
      }, 30000);
    }
  }

  /**
   * Log if debug mode is enabled.
   */
  private log(...args: unknown[]): void {
    if (this.debug) {
      console.log('[ClientTableMaster]', ...args);
    }
  }
}

// ===========================================
// SINGLETON INSTANCE
// ===========================================

let clientTableMasterInstance: ClientTableMaster | null = null;

/**
 * Get or create the ClientTableMaster singleton.
 */
export function getClientTableMaster(options?: ClientTableMasterOptions): ClientTableMaster {
  if (!clientTableMasterInstance) {
    clientTableMasterInstance = new ClientTableMaster(options);
  } else if (options) {
    // Update callbacks if provided
    if (options.onBatchReady) {
      clientTableMasterInstance.setBatchHandler(options.onBatchReady);
    }
    if (options.onRoundAck) {
      clientTableMasterInstance.setRoundAckHandler(options.onRoundAck);
    }
  }
  return clientTableMasterInstance;
}

/**
 * Reset the singleton (for testing or table changes).
 */
export function resetClientTableMaster(): void {
  if (clientTableMasterInstance) {
    clientTableMasterInstance.flushBalance();
    clientTableMasterInstance.flushSkipLog(); // Send any pending skip logs before reset
    clientTableMasterInstance.clearHistory();
  }
  clientTableMasterInstance = null;
}

/**
 * Debug helper - expose on window in development.
 */
if (typeof window !== 'undefined') {
  (window as any).clientTableMaster = {
    getInstance: () => clientTableMasterInstance,
    getSummary: () => clientTableMasterInstance?.getSummary(),
    getState: () => clientTableMasterInstance?.getState(),
    getQueueSize: () => clientTableMasterInstance?.getQueueSize(),
    getDiscarded: () => clientTableMasterInstance?.getDiscardedEvents(),
    setEnabled: (enabled: boolean) => clientTableMasterInstance?.setEnabled(enabled),
    // NOTE: This is the EVENT ORCHESTRATOR
    // It unpacks batches, orders events, and enforces sequence
    role: 'EVENT_ORCHESTRATOR',
    // Balance tracking helpers
    registerBalanceAnimation: (eventType: string, amount: number, seat?: number) =>
      clientTableMasterInstance?.registerBalanceAnimation(eventType, amount, seat),
    balanceAnimationComplete: () =>
      clientTableMasterInstance?.balanceAnimationComplete(),
    flushBalance: () =>
      clientTableMasterInstance?.flushBalance(),
    // Skip log helpers
    getSkipLogCount: () =>
      clientTableMasterInstance?.getSkipLogCount(),
    flushSkipLog: () =>
      clientTableMasterInstance?.flushSkipLog(),
    // Away mode helpers (resync support)
    enterAwayMode: () =>
      clientTableMasterInstance?.enterAwayMode(),
    exitAwayMode: (round?: number, phase?: GamePhase) =>
      clientTableMasterInstance?.exitAwayMode(round, phase),
    isInAwayMode: () =>
      clientTableMasterInstance?.isInAwayMode(),
    getAwayModeQueueCount: () =>
      clientTableMasterInstance?.getAwayModeQueueCount(),
  };
}
